import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ShieldAlert, 
  MessageSquare, 
  FileText, 
  Archive, 
  Check, 
  X, 
  VolumeX, 
  Eye, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import { apiWorker } from '../../api/worker';
import { Card, Badge, Button, Spinner, Modal } from '../ui';
import { toast } from 'react-hot-toast';
import { cn } from '../../lib/utils';
import { usePermissions } from '../../hooks/usePermissions';

interface ModerationItem {
  id: string;
  contentType: 'MESSAGE' | 'TOPIC' | 'VAULT_ASSET';
  contentId: string;
  title: string;
  snippet: string;
  authorId: string;
  authorName: string;
  nodeId: string;
  nodeName: string;
  flagReason: string;
  status: 'PENDING' | 'QUARANTINED' | 'VERIFIED' | 'DISMISSED';
  isQuarantined: boolean;
  createdAt: string;
}

export const AdminModerationManager: React.FC = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isSupportAdmin } = usePermissions();
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'QUARANTINED'>('PENDING');
  const [page, setPage] = useState(0);

  // Mute modal state
  const [muteTarget, setMuteTarget] = useState<{ userId: string; authorName: string } | null>(null);
  const [muteHours, setMuteHours] = useState(24);

  // Fetch moderation queue
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'moderation', filter, page],
    queryFn: async () => {
      const res = await apiWorker.get(`/api/admin/moderation/queue?status=${filter}&page=${page}&size=10`);
      return res.data;
    }
  });

  const items: ModerationItem[] = Array.isArray(data?.content) 
    ? data.content 
    : (Array.isArray(data) ? data : []);
  const totalPages = data?.totalPages || 1;

  // Quarantine mutation
  const quarantineMutation = useMutation({
    mutationFn: async ({ contentType, contentId, isQuarantined }: { contentType: string; contentId: string; isQuarantined: boolean }) => {
      await apiWorker.patch('/api/admin/moderation/content/quarantine', {
        contentType,
        contentId,
        isQuarantined
      });
    },
    onSuccess: (_, vars) => {
      toast.success(vars.isQuarantined ? 'Content Quarantined' : 'Content Restored');
      queryClient.invalidateQueries({ queryKey: ['admin', 'moderation'] });
    },
    onError: () => toast.error('Failed to update content quarantine status')
  });

  // Mute user mutation
  const muteMutation = useMutation({
    mutationFn: async ({ userId, hours }: { userId: string; hours: number }) => {
      await apiWorker.post(`/api/admin/moderation/users/${userId}/mute`, {
        durationMinutes: hours * 60
      });
    },
    onSuccess: (_, vars) => {
      toast.success(`User muted for ${vars.hours === 999999 ? 'permanent' : vars.hours + ' hours'}`);
      setMuteTarget(null);
    },
    onError: () => toast.error('Failed to mute user')
  });

  // Dismiss flag mutation
  const dismissMutation = useMutation({
    mutationFn: async ({ contentType, contentId }: { contentType: string; contentId: string }) => {
      await apiWorker.post('/api/admin/moderation/dismiss', {
        contentType,
        contentId
      });
    },
    onSuccess: () => {
      toast.success('Flag dismissed successfully');
      queryClient.invalidateQueries({ queryKey: ['admin', 'moderation'] });
    },
    onError: () => toast.error('Failed to dismiss flag')
  });

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'MESSAGE': return <MessageSquare size={14} className="text-blue-500" />;
      case 'TOPIC': return <FileText size={14} className="text-amber-500" />;
      case 'VAULT_ASSET': return <Archive size={14} className="text-purple-500" />;
      default: return <ShieldAlert size={14} />;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldAlert size={20} className="text-red-500" /> Content Moderation & Safety Queue
          </h2>
          <p className="text-xs text-gray-500">Review flagged user messages, discussion topics, and compliance vault assets.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => refetch()} 
            className="gap-2 text-xs"
          >
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b-2 border-black dark:border-white pb-2">
        {(['PENDING', 'QUARANTINED', 'ALL'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => {
              setFilter(tab);
              setPage(0);
            }}
            className={cn(
              "px-4 py-2 text-xs font-mono font-bold uppercase tracking-wider transition-colors",
              filter === tab 
                ? "bg-black text-white dark:bg-white dark:text-black" 
                : "bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100"
            )}
          >
            {tab === 'PENDING' ? 'Needs Review' : tab === 'QUARANTINED' ? 'Quarantined' : 'All Items'}
          </button>
        ))}
      </div>

      {/* Queue Table */}
      <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner size="md" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 space-y-2">
            <Check size={36} className="mx-auto text-green-500" />
            <p className="font-bold text-base text-gray-900 dark:text-white">Moderation Queue Clear</p>
            <p className="text-xs text-gray-500 font-mono">No items currently match the "{filter}" safety filter.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b-2 border-black dark:border-white font-mono uppercase text-gray-500">
                    <th className="py-2.5 px-3">Type</th>
                    <th className="py-2.5 px-3">Content / Snippet</th>
                    <th className="py-2.5 px-3">Organization</th>
                    <th className="py-2.5 px-3">Author</th>
                    <th className="py-2.5 px-3">Flag Reason</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5 font-mono uppercase font-bold text-[10px]">
                          {getContentTypeIcon(item.contentType)}
                          <span>{item.contentType}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 max-w-xs">
                        <p className="font-bold text-gray-900 dark:text-white truncate">{item.title}</p>
                        <p className="text-gray-500 font-mono text-[11px] truncate">{item.snippet || 'No excerpt'}</p>
                      </td>
                      <td className="py-3 px-3 font-mono text-[11px]">
                        {item.nodeName}
                      </td>
                      <td className="py-3 px-3">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 block truncate max-w-[120px]">
                          {item.authorName || item.authorId}
                        </span>
                        <span className="text-[10px] text-gray-400 font-mono">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-3 px-3 font-mono text-[11px] text-red-600 dark:text-red-400">
                        {item.flagReason}
                      </td>
                      <td className="py-3 px-3">
                        <Badge 
                          variant={item.isQuarantined || item.status === 'QUARANTINED' ? 'red' : item.status === 'PENDING' ? 'amber' : 'green'}
                          className="text-[10px] uppercase font-mono"
                        >
                          {item.isQuarantined ? 'Quarantined' : item.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {item.isQuarantined ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="text-[10px] py-1 px-2 border-2 border-black dark:border-white"
                              onClick={() => quarantineMutation.mutate({
                                contentType: item.contentType,
                                contentId: item.contentId,
                                isQuarantined: false
                              })}
                              disabled={quarantineMutation.isPending}
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="text-[10px] py-1 px-2 text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => quarantineMutation.mutate({
                                contentType: item.contentType,
                                contentId: item.contentId,
                                isQuarantined: true
                              })}
                              disabled={quarantineMutation.isPending}
                            >
                              Quarantine
                            </Button>
                          )}

                          <Button
                            variant="secondary"
                            size="sm"
                            className="text-[10px] py-1 px-2 border-2 border-black dark:border-white gap-1"
                            onClick={() => setMuteTarget({ userId: item.authorId, authorName: item.authorName || item.authorId })}
                          >
                            <VolumeX size={12} /> Mute
                          </Button>

                          <Button
                            variant="secondary"
                            size="sm"
                            className="text-[10px] py-1 px-2 text-gray-500 hover:text-black"
                            onClick={() => dismissMutation.mutate({
                              contentType: item.contentType,
                              contentId: item.contentId
                            })}
                            disabled={dismissMutation.isPending}
                            title="Dismiss flag"
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t-2 border-black dark:border-white pt-4">
                <span className="text-xs font-mono text-gray-500">
                  Page <span className="font-bold text-black dark:text-white">{page + 1}</span> of <span className="font-bold text-black dark:text-white">{totalPages}</span>
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    disabled={page === 0} 
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    className="gap-1 px-3 text-xs"
                  >
                    <ChevronLeft size={14} /> Prev
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    disabled={page >= totalPages - 1} 
                    onClick={() => setPage(p => p + 1)}
                    className="gap-1 px-3 text-xs"
                  >
                    Next <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Mute Modal */}
      {muteTarget && (
        <Modal 
          isOpen={true} 
          onClose={() => setMuteTarget(null)}
          title={`Mute User: ${muteTarget.authorName}`}
        >
          <div className="space-y-4 p-2">
            <p className="text-xs text-gray-600 dark:text-gray-300 font-mono">
              Temporarily or permanently disable chat and posting capabilities for user ID: <span className="font-bold">{muteTarget.userId}</span>.
            </p>
            <div>
              <label className="block text-xs font-bold font-mono uppercase mb-2">Duration</label>
              <select 
                value={muteHours}
                onChange={e => setMuteHours(Number(e.target.value))}
                className="w-full border-2 border-black dark:border-white p-2 text-xs font-mono bg-white dark:bg-slate-900"
              >
                <option value={24}>24 Hours</option>
                <option value={168}>7 Days (1 Week)</option>
                <option value={720}>30 Days</option>
                <option value={999999}>Permanent</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t-2 border-black dark:border-white">
              <Button variant="secondary" onClick={() => setMuteTarget(null)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                className="bg-red-600 hover:bg-red-700 text-white border-2 border-black"
                onClick={() => muteMutation.mutate({ userId: muteTarget.userId, hours: muteHours })}
                disabled={muteMutation.isPending}
              >
                Confirm Mute
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
